import { FC, useState } from 'react';
import { Modal } from 'components/Modal';
import { Title } from 'components/Title';
import { Button } from 'ui/Button';
import styles from './ModalConclusion.module.scss';
import Bunner from 'ui/Bunner';
import { Label } from 'components/CreateOrderForm';
import { Loader, NumberInput, Textarea } from '@mantine/core';
import {
  useCreateRefundRequestMutation,
  useGetProfileQuery,
} from 'redux/Api/profileApi/ProfileApi';
import { BodyCreateRefundRequest } from 'redux/Api/profileApi/type';

interface ModalConclusionTypes {
  setShowModalConclusion: (e: boolean) => void;
}

export const ModalConclusion: FC<ModalConclusionTypes> = ({ setShowModalConclusion }) => {
  const [step, setStep] = useState<'select' | 'completed'>('select');
  const [textSuccess, setTextSuccess] = useState<string>('');
  const { data: profileData } = useGetProfileQuery();

  const [values, setValues] = useState<{
    amount: number | null;
    recipient_account: string;
    bank_code: string;
    recipient_name: string;
    recipient_inn: string;
  }>({
    amount: null,
    recipient_account: '',
    bank_code: '',
    recipient_name: profileData?.customer?.name || '',
    recipient_inn: profileData?.customer?.inn || '',
  });
  const [error, setError] = useState<{
    recipient_name: string;
    recipient_inn: string;
    bank_code: string;
    recipient_account: string;
    amount: string;
  }>({
    recipient_name: '',
    recipient_inn: '',
    bank_code: '',
    recipient_account: '',
    amount: '',
  });
  const [errorMasseg, setErrorMasseg] = useState<string>('');
  const [createRefundRequest, { isLoading }] = useCreateRefundRequestMutation();

  const handleCreateRefundRequest = async (values: BodyCreateRefundRequest) => {
    try {
      const res = await createRefundRequest(values).unwrap();

      setTextSuccess(res.message || 'Запрос на возврат отправлен');
      setStep('completed');
    } catch (error) {
      console.error(error);
      const data = (
        error as { data?: { errors?: { [key: string]: string[] }; message?: string } }
      ).data;
      const errorData = data?.errors;
      if (!errorData && data?.message) {
        setErrorMasseg(data.message);
      }
      if (errorData) {
        setError({
          recipient_name: errorData.recipient_name?.[0] || '',
          recipient_inn: errorData.recipient_inn?.[0] || '',
          bank_code: errorData.bank_code?.[0] || '',
          recipient_account: errorData.recipient_account?.[0] || '',
          amount: errorData.amount?.[0] || '',
        });
      }
    }
  };

  const renderSelectStep = () => (
    <>
      <Title className={styles.root_title} title='Реквизиты для возврата средств' />
      <Bunner
        title='Внимание!'
        description='После рассмотрения заявки денежные средства вернутся на указанные реквизиты. Срок зачисления зависит от вашего банка'
        color='#FFF5E7'
      />
      <div className={styles.root_formBlock}>
        <div>
          <Label text='Получатель' />
          <Textarea
            placeholder='Введи получателя'
            required
            value={values.recipient_name}
            autosize
            minRows={1}
            maxRows={4}
            onChange={(e) => {
              setValues({ ...values, recipient_name: e.target.value });
              setError({ ...error, recipient_name: '' });
            }}
            styles={{
              input: {
                display: 'flex',
                alignItems: 'center',
                paddingTop: '12px',
                paddingBottom: '12px',
                fontSize: '16px',
                border: error.recipient_name ? '1px solid red' : 'none',
              },
            }}
          />
          {error.recipient_name && (
            <div className={styles.error}>{error.recipient_name}</div>
          )}
        </div>

        <div className={styles.inn_bank_codeBlock}>
          <div style={{ width: '50%' }}>
            <Label text='ИНН' />
            <NumberInput
              placeholder='Введи ИНН'
              required
              value={values.recipient_inn}
              hideControls
              maxLength={12}
              allowNegative={false}
              allowDecimal={false}
              trimLeadingZeroesOnBlur={false}
              onChange={(val) => {
                setValues({ ...values, recipient_inn: String(val) });
                setError({ ...error, recipient_inn: '' });
              }}
              styles={{
                input: {
                  height: '48px',
                  fontSize: '16px',
                  border: error.recipient_inn ? '1px solid red' : 'none',
                },
              }}
            />
            {error.recipient_inn && (
              <div className={styles.error}>{error.recipient_inn}</div>
            )}
          </div>

          <div style={{ width: '50%' }}>
            <Label text='БИК банка' />
            <NumberInput
              placeholder='Введи БИК'
              required
              value={values.bank_code}
              hideControls
              maxLength={9}
              allowNegative={false}
              allowDecimal={false}
              trimLeadingZeroesOnBlur={false}
              onChange={(val) => {
                setValues({ ...values, bank_code: String(val) });
                setError({ ...error, bank_code: '' });
              }}
              styles={{
                input: {
                  height: '48px',
                  fontSize: '16px',
                  border: error.bank_code ? '1px solid red' : 'none',
                },
              }}
            />
            {error.bank_code && <div className={styles.error}>{error.bank_code}</div>}
          </div>
        </div>

        <div>
          <Label text='Номер счета' />
          <NumberInput
            placeholder='Введи номер счета'
            required
            value={values.recipient_account}
            hideControls
            maxLength={20}
            allowNegative={false}
            allowDecimal={false}
            trimLeadingZeroesOnBlur={false}
            onChange={(val) => {
              setValues({ ...values, recipient_account: String(val) });
              setError({ ...error, recipient_account: '' });
            }}
            styles={{
              input: {
                height: '48px',
                fontSize: '16px',
                border: error.recipient_account ? '1px solid red' : 'none',
              },
            }}
          />
          {error.recipient_account && (
            <div className={styles.error}>{error.recipient_account}</div>
          )}
        </div>

        <div>
          <Label text='Сумма' />
          <NumberInput
            placeholder='0'
            value={values.amount || ''}
            hideControls
            allowNegative={false}
            min={0}
            onChange={(val) => {
              setValues({ ...values, amount: val as number });
              setError({ ...error, amount: '' });
            }}
            styles={{
              input: {
                height: '48px',
                fontSize: '16px',
                border: error.amount ? '1px solid red' : 'none',
              },
            }}
          />
          {error.amount && <div className={styles.error}>{error.amount}</div>}
        </div>
      </div>
      <div className={styles.root_infoBlock}>
        <Button
          disabled={isLoading}
          className={styles.root_btn}
          theme='filed'
          size='L'
          onClick={() => handleCreateRefundRequest(values)}>
          <div className={styles.textBtnContent}>
            Запросить возврат
            {isLoading && <Loader size='sm' color='white' />}
          </div>
        </Button>
        {errorMasseg && <div className={styles.error}>{errorMasseg}</div>}
      </div>
    </>
  );

  const renderCompletedStep = () => (
    <>
      <Title className={styles.root_title} title='Уведомление' />
      <p className={styles.root_text}>{textSuccess}</p>
      <div className={styles.root_actions}>
        <Button
          onClick={() => setShowModalConclusion(false)}
          className={styles.root_closeBtn}
          theme='filed'
          size='L'>
          Закрыть
        </Button>
      </div>
    </>
  );

  return (
    <Modal className={styles.cross} setOpen={setShowModalConclusion}>
      <div className={styles.root}>
        {step === 'completed' && renderCompletedStep()}
        {step === 'select' && renderSelectStep()}
      </div>
    </Modal>
  );
};
